import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Save } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';

interface EmailPreferencesData {
  _id?: string;
  emailCategories: {
    generic?: boolean;
    feature_education?: boolean;
    usage_achievement?: boolean;
    underutilization?: boolean;
    upgrade_prompt?: boolean;
  };
  unsubscribedAll: boolean;
}

export const EmailPreferences: React.FC = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<EmailPreferencesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchPreferences = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/edm/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.data.preferences) {
        setPreferences(data.data.preferences);
      } else {
        // Default preferences (all subscribed)
        setPreferences({
          emailCategories: {
            generic: true,
            feature_education: true,
            usage_achievement: true,
            underutilization: true,
            upgrade_prompt: true
          },
          unsubscribedAll: false
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // Default preferences on error
      setPreferences({
        emailCategories: {
          generic: true,
          feature_education: true,
          usage_achievement: true,
          underutilization: true,
          upgrade_prompt: true
        },
        unsubscribedAll: false
      });
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleSave = async () => {
    if (!preferences) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/edm/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emailCategories: preferences.emailCategories,
          unsubscribedAll: preferences.unsubscribedAll
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        alert(data.message || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCategory = (category: keyof EmailPreferencesData['emailCategories']) => {
    if (!preferences) return;
    
    setPreferences({
      ...preferences,
      emailCategories: {
        ...preferences.emailCategories,
        [category]: !preferences.emailCategories[category]
      },
      unsubscribedAll: false // Uncheck global unsubscribe if toggling individual category
    });
  };

  const handleUnsubscribeAll = () => {
    if (!preferences) return;
    
    setPreferences({
      ...preferences,
      unsubscribedAll: !preferences.unsubscribedAll,
      emailCategories: preferences.unsubscribedAll ? {
        generic: true,
        feature_education: true,
        usage_achievement: true,
        underutilization: true,
        upgrade_prompt: true
      } : {
        generic: false,
        feature_education: false,
        usage_achievement: false,
        underutilization: false,
        upgrade_prompt: false
      }
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500 dark:text-gray-400">{t('common.loading') || 'Loading...'}</div>
      </div>
    );
  }

  if (!preferences) {
    return null;
  }

  const categories = [
    { key: 'generic' as const, label: t('edm.preferences.categoryGeneric') || 'Generic Marketing' },
    { key: 'feature_education' as const, label: t('edm.preferences.categoryFeatureEducation') || 'Feature Education' },
    { key: 'usage_achievement' as const, label: t('edm.preferences.categoryUsageAchievement') || 'Usage Achievements' },
    { key: 'underutilization' as const, label: t('edm.preferences.categoryUnderutilization') || 'Underutilization Alerts' },
    { key: 'upgrade_prompt' as const, label: t('edm.preferences.categoryUpgradePrompt') || 'Upgrade Prompts' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <Mail className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('edm.preferences.title') || 'Email Preferences'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('edm.preferences.description') || 'Manage which types of emails you receive from us'}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        {/* Unsubscribe All */}
        <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.unsubscribedAll}
              onChange={handleUnsubscribeAll}
              className="w-5 h-5 text-accent-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-900 dark:text-white">
                {t('edm.preferences.unsubscribeAll') || 'Unsubscribe from all marketing emails'}
              </span>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('edm.preferences.unsubscribeAllDescription') || 'You will stop receiving all marketing emails, but may still receive important account notifications'}
              </p>
            </div>
          </label>
        </div>

        {/* Category Preferences */}
        {!preferences.unsubscribedAll && (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">
              {t('edm.preferences.categoryPreferences') || 'Email Categories'}
            </h3>
            {categories.map(category => (
              <label key={category.key} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">{category.label}</span>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.emailCategories[category.key] || false}
                  onChange={() => handleToggleCategory(category.key)}
                  className="w-5 h-5 text-accent-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </label>
            ))}
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6 flex items-center justify-between">
          {success && (
            <div className="text-success-600 dark:text-success-400 text-sm">
              {t('edm.preferences.saved') || 'Preferences saved successfully!'}
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="ml-auto flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            <span>{saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
